import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Link2, MonitorSmartphone } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorState,
  FullPageSpinner,
  cx,
  Badge,
  KeyValue,
  Button,
  buttonClass,
  Breadcrumb,
  Section,
} from "@/components/ui";
import { domainKeys, domainsApi, useDomainDetail, type Domain, type ListFilters } from "@/lib/domains";
import type { Site } from "@/lib/types";

import { ConnectError, RadioCard, siteKindLabel, useSites } from "./connect/shared";
import { DomainIcon, Heading, HeadTile, DomainPage, TwoColumns } from "@/components/domains/parts";

const EXTERNAL = "external";
const ACTIVE: ListFilters = { status: "active", sort: "name", page_size: 100 };

/** Figma "Connect a domain to your website" (4063-1039), plus a website step when there are several. */
export function ConnectDomainPage() {
  const { domainId = "" } = useParams();
  const { orgId } = useOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const detail = useDomainDetail(domainId);
  const list = useQuery({
    queryKey: domainKeys.list(orgId, ACTIVE),
    queryFn: () => domainsApi.list(orgId, ACTIVE),
  });
  const sites = useSites();

  const [picked, setPicked] = useState<string | null>(null);
  const [pickedSite, setPickedSite] = useState<string | null>(null);
  const [primary, setPrimary] = useState(true);

  const connect = useMutation({
    mutationFn: ({ id, siteId }: { id: string; siteId: string }) =>
      domainsApi.connect(orgId, id, siteId, primary),
    onSuccess: async (_, { id }) => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      navigate(`/domains/${id}/connection`);
    },
  });

  if (detail.isPending || list.isPending || sites.isPending) return <FullPageSpinner />;
  const failed = detail.error ?? list.error ?? sites.error;
  if (failed)
    return (
      <ErrorState
        error={failed}
        onRetry={() => {
          void detail.refetch();
          void list.refetch();
          void sites.refetch();
        }}
      />
    );

  const current = detail.data!.domain;
  // The URL's domain first, then the rest of the workspace's active domains.
  const domains: Domain[] = [current, ...list.data!.domains.filter((d) => d.id !== current.id)].filter(
    (d) => d.status === "active",
  );
  const allSites = sites.data!;
  const selectedId =
    picked ?? (domains.some((d) => d.id === current.id) ? current.id : (domains[0]?.id ?? EXTERNAL));
  const selected = domains.find((d) => d.id === selectedId) ?? null;
  const defaultSiteId =
    (selected?.site_id && allSites.some((s) => s.id === selected.site_id) ? selected.site_id : null) ??
    allSites[0]?.id ??
    null;
  const siteId = pickedSite ?? defaultSiteId;
  const site = allSites.find((s) => s.id === siteId) ?? null;
  const siteName = site ? site.name : "your";
  const primaryOfSite = site
    ? [current, ...list.data!.domains].find(
        (d) => d.site_id === site.id && d.is_primary && d.connection === "connected",
      )
    : undefined;
  const customNs = selected?.nameserver_mode === "custom";

  const submit = () => {
    if (selectedId === EXTERNAL) {
      navigate(`/domains/connect-external${site ? `?site=${encodeURIComponent(site.id)}` : ""}`);
      return;
    }
    if (selected && site) connect.mutate({ id: selected.id, siteId: site.id });
  };

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "...", to: "/domains" },
          { label: current.display_name, to: `/domains/${current.id}` },
          { label: "connect domain" },
        ]}
      />
      <Heading
        tile={
          <HeadTile tone="success">
            <DomainIcon name="link" size={22} />
          </HeadTile>
        }
        title="Connect a domain to your website"
        subtitle={`Choose which domain should point to ${site ? `${siteName}'s` : "your"} website`}
      />

      {allSites.length === 0 ? (
        <Section title="You don't have a website yet">
          <p className="text-sm text-fg-subtle">
            Build your website, or upload one on Hosting, and then come back to connect {current.display_name}{" "}
            to it.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <Link to="/websites" className={buttonClass("brand", "lg")}>
              Go to Website
            </Link>
            <Link to="/hosting" className={buttonClass("secondary", "lg")}>
              Go to Hosting
            </Link>
          </div>
        </Section>
      ) : (
        <>
          <TwoColumns
            main={
              <>
                <Section title="1. Choose a domain">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    <div
                      role="radiogroup"
                      aria-label="Domain"
                      className="flex min-w-0 flex-1 flex-col gap-2.5"
                    >
                      {domains.map((d) => (
                        <RadioCard
                          key={d.id}
                          name="domain"
                          checked={selectedId === d.id}
                          onSelect={() => {
                            setPicked(d.id);
                            setPickedSite(null);
                          }}
                          tileClass={selectedId === d.id ? "bg-[#12a97e]" : "bg-[#938a85]"}
                          tile={<Globe className="size-[18px]" aria-hidden />}
                          title={d.display_name}
                          detail={domainDetail(d, site)}
                        />
                      ))}
                      <RadioCard
                        name="domain"
                        checked={selectedId === EXTERNAL}
                        onSelect={() => setPicked(EXTERNAL)}
                        tileClass="bg-[#f4b53f]"
                        tile={<Link2 className="size-[18px]" aria-hidden />}
                        title="Connect a domain I own elsewhere"
                        detail="Enter a domain registered with another provider"
                      />
                    </div>
                    <WhatWillHappen
                      domain={selected}
                      site={site}
                      primary={primary}
                      onPrimary={setPrimary}
                      customNs={customNs}
                    />
                  </div>
                </Section>

                {allSites.length > 1 && (
                  <Section title="2. Choose a website" subtitle="Which of your sites this domain should open">
                    <div role="radiogroup" aria-label="Website" className="grid gap-2.5 md:grid-cols-2">
                      {allSites.map((s) => (
                        <RadioCard
                          key={s.id}
                          name="site"
                          checked={siteId === s.id}
                          onSelect={() => setPickedSite(s.id)}
                          tileClass={s.kind === "builder" ? "bg-brand-500" : "bg-[#5c5551]"}
                          tile={<MonitorSmartphone className="size-[18px]" aria-hidden />}
                          title={s.name}
                          detail={`${siteKindLabel(s)} · ${s.hostname}`}
                        />
                      ))}
                    </div>
                  </Section>
                )}
              </>
            }
            side={
              <Section title="Current setup">
                {site && allSites.length === 1 && (
                  <KeyValue variant="detail" label="Website">
                    <span className="truncate text-sm font-medium text-fg">{site.name}</span>
                  </KeyValue>
                )}
                <KeyValue variant="detail" label="Website address">
                  {site ? (
                    <Badge size="md" tone="neutral">
                      {site.hostname}
                    </Badge>
                  ) : (
                    <Badge size="md" tone="neutral">
                      —
                    </Badge>
                  )}
                </KeyValue>
                <KeyValue variant="detail" label="Primary domain">
                  {primaryOfSite ? (
                    <Badge size="md" tone="success">
                      {primaryOfSite.display_name}
                    </Badge>
                  ) : (
                    <Badge size="md" tone="warning">
                      None set
                    </Badge>
                  )}
                </KeyValue>
              </Section>
            }
          />

          {connect.error && <ConnectError error={connect.error} domainId={selected?.id} />}
          <div className="flex flex-wrap gap-2.5">
            <Button
              variant="brand"
              size="lg"
              onClick={submit}
              loading={connect.isPending}
              requires="domains:manage"
              disabledReason={
                !site
                  ? "Choose a website to connect to"
                  : selectedId !== EXTERNAL && !selected
                    ? "Choose a domain"
                    : selectedId !== EXTERNAL && customNs
                      ? "This domain uses other nameservers. Switch it back to CortexGrip nameservers first."
                      : undefined
              }
            >
              {selectedId === EXTERNAL ? "Continue" : "Connect domain"}
            </Button>
            <Link to={`/domains/${current.id}`} className={buttonClass("secondary", "lg")}>
              Cancel
            </Link>
          </div>
        </>
      )}
    </DomainPage>
  );
}

function domainDetail(d: Domain, site: Site | null): string {
  const registered = d.origin === "transferred" ? "Transferred to CortexGrip" : "Registered with CortexGrip";
  if (d.nameserver_mode === "custom") return `${registered} · uses other nameservers`;
  if (!d.site_id) return `${registered} · not yet connected`;
  const name = d.site?.name ?? "a website";
  if (d.connection === "connected")
    return `${registered} · connected to ${d.site_id === site?.id ? "this website" : name}`;
  if (d.connection === "error") return `${registered} · connection to ${name} needs a fix`;
  return `${registered} · connecting to ${name}`;
}

/** The green box beside the choices: what connecting will do. */
function WhatWillHappen({
  domain,
  site,
  primary,
  onPrimary,
  customNs,
}: {
  domain: Domain | null;
  site: Site | null;
  primary: boolean;
  onPrimary: (on: boolean) => void;
  customNs: boolean;
}) {
  const box = "flex w-full flex-col gap-3 rounded-xl p-3.5 lg:w-[264px]";
  if (!domain)
    return (
      <div className={cx(box, "bg-[#faf6ef]")}>
        <p className="text-sm font-semibold text-fg">What will happen</p>
        <p className="text-xs leading-[18px] text-[#5c5551]">
          You'll enter your domain and we'll show the DNS records to add at the company it's registered with.
          Your domain stays where it is.
        </p>
      </div>
    );
  if (customNs)
    return (
      <div className={cx(box, "bg-[#fef4e5]")}>
        <p className="text-sm font-semibold text-fg">Uses other nameservers</p>
        <p className="text-xs leading-[18px] text-[#5c5551]">
          CortexGrip can only set up {domain.display_name}'s records automatically while it uses CortexGrip
          nameservers.
        </p>
        <Link
          to={`/domains/${domain.id}?tab=nameservers`}
          className="text-xs font-semibold text-[#995c05] underline-offset-2 hover:underline"
        >
          Open nameservers
        </Link>
      </div>
    );
  const moving = domain.site_id && site && domain.site_id !== site.id ? domain.site?.name : null;
  return (
    <div className={cx(box, "bg-[#eef8f3]")}>
      <p className="text-sm font-semibold text-fg">What will happen</p>
      <p className="text-xs leading-[18px] text-[#5c5551]">
        {primary
          ? `${domain.display_name} will become your website's primary custom domain, replacing the CortexGrip subdomain as the main address.`
          : `${domain.display_name} will open your website too, alongside its current address.`}{" "}
        DNS updates automatically - no manual setup needed.
        {moving ? ` It will be disconnected from ${moving} first.` : ""}
      </p>
      <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-fg">
        <input
          type="checkbox"
          checked={primary}
          onChange={(e) => onPrimary(e.target.checked)}
          className="size-3.5 accent-[#12a97e]"
        />
        Make this my primary domain
      </label>
    </div>
  );
}
